import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    FaSearch, FaBell, FaChevronDown, FaHome, FaNetworkWired,
    FaFlask, FaBookOpen, FaUsers, FaCog, FaPlus, FaRegIdCard,
    FaUserCheck, FaExclamationTriangle, FaFileMedical, FaChartLine,
    FaEdit, FaVial, FaFileAlt, FaLock, FaUsersCog, FaDatabase,
    FaRegLightbulb, FaRocket, FaFileDownload, FaUserAstronaut,
    FaLaptopMedical, FaCommentMedical, FaWrench, FaKeyboard,
    FaQuestionCircle, FaChartBar as FaChartBarAlt, FaWallet
} from 'react-icons/fa';
import './ClinicalProjects.css';

// ── MOCK DATA ─────────────────────────────────────────────────────────────
const MOCK_USER = {
    name: 'Dr. Investigador',
    role: 'Investigador Clínico Principal',
};

const STATS = [
    { label: 'Proyectos Activos', value: '12', trend: '+5% vs mes ant.', icon: <FaRegIdCard />, color: '#FF6600' },
    { label: 'Total Pacientes', value: '1,240', trend: '+12% recambio', icon: <FaUserCheck />, color: '#FF6600' },
    { label: 'Alertas de Seguridad', value: '2', trend: 'Requiere atención', icon: <FaExclamationTriangle />, color: '#ef4444' },
];

const CLINICAL_TRIALS = [
    {
        id: 'CP-2024-08',
        status: 'RECLUTANDO',
        statusColor: '#FF6600',
        title: 'Eficacia de Inmunoterapia en Cáncer de Pulmón Fase III',
        description: 'Estudio multicéntrico sobre respuesta inmune en estadios avanzados.',
        patients: 85,
        totalPatients: 100,
        nextMilestone: 'Cierre de Cohorte A',
        progress: 75,
        finished: false,
    },
    {
        id: 'CP-2023-14',
        status: 'ACTIVO',
        statusColor: '#10b981',
        title: 'Seguimiento de Biomarcadores en Diabetes Tipo 2',
        description: 'Evaluación de nuevos sensores continuos de glucosa.',
        patients: 120,
        totalPatients: 120,
        nextMilestone: 'Finaliza en 14 días',
        progress: 100,
        finished: false,
    },
    {
        id: 'CP-2022-03',
        status: 'FINALIZADO',
        statusColor: '#6b7280',
        title: 'Evaluación de Terapia Génica en Hemofilia B',
        description: 'Estudio de fase II completado con resultados positivos.',
        patients: 60,
        totalPatients: 60,
        nextMilestone: 'Publicación de resultados',
        progress: 100,
        finished: true,
    }
];

const AI_INSIGHTS = [
    {
        title: 'Sugerencia de Reclutamiento',
        desc: 'El proyecto CP-2024-08 ha bajado su ritmo. Sugiero revisar criterios de exclusión en centros del Sur.',
        link: 'Ver análisis detallado',
        icon: <FaRegLightbulb />
    },
    {
        title: 'Optimización de Datos',
        desc: 'Detectadas anomalías en eCRF de diabetes. Posible error de calibración en sensores B-4.',
        icon: <FaDatabase />
    }
];

const QUICK_RESOURCES = [
    { label: 'Vademécum Eco', icon: <FaLaptopMedical /> },
    { label: 'Foro de Investigadores', icon: <FaCommentMedical /> },
    { label: 'Exportar Reportes', icon: <FaFileDownload /> },
];

const NAV_ITEMS = [
    { key: 'inicio', label: 'Inicio', icon: <FaHome />, path: '/researcher-community' },
    { key: 'red', label: 'Mi Red', icon: <FaNetworkWired />, path: '/researcher-network' },
    { key: 'proyectos', label: 'Proyectos Clínicos', icon: <FaFlask />, path: '/clinical-projects' },
    { key: 'publicaciones', label: 'Publicaciones', icon: <FaBookOpen />, path: null },
    { key: 'grupos', label: 'Grupos Colaborativos', icon: <FaUsers />, path: null },
    { key: 'config', label: 'Configuración', icon: <FaCog />, path: null },
];

const ClinicalProjects = () => {
    const navigate = useNavigate();
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const activeNav = 'proyectos';

    return (
        <div className="cp-page">
            {/* ── LEFT SIDEBAR (Standard Portal Style) ── */}
            <aside className="cp-sidebar-left">
                <div className="cp-sidebar-top">
                    <Link to="/" className="cp-logo">
                        <img src="/logoecseia.png" alt="Logo" className="cp-logo-img" />
                        <span className="cp-logo-eco">Eco</span><span className="cp-logo-rest">ScienceInnova</span>
                    </Link>

                    <nav className="cp-nav">
                        {NAV_ITEMS.map(item => (
                            <button
                                key={item.key}
                                className={`cp-nav-item ${activeNav === item.key ? 'active' : ''}`}
                                onClick={() => item.path && navigate(item.path)}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="cp-sidebar-bottom">
                    <button className="cp-new-btn" onClick={() => navigate('/researcher-ideation')}>
                        <FaPlus /> Nueva Idea
                    </button>
                </div>
            </aside>

            {/* ── MAIN CONTENT ── */}
            <div className="cp-main-area">
                {/* HEADER */}
                <header className="cp-header">
                    <div className="cp-header-left">
                        <nav className="cp-header-nav">
                            <span className="cp-h-nav-item">Inicio</span>
                            <span className="cp-h-nav-item">Comunidad</span>
                            <span className="cp-h-nav-item">Recursos</span>
                            <span className="cp-h-nav-item">Membresías</span>
                            <span className="cp-h-nav-item">Contacto</span>
                        </nav>
                    </div>

                    <div className="cp-header-right">
                        <div className="cp-search">
                            <FaSearch className="cp-search-icon" />
                            <input placeholder="Buscar protocolos..." className="cp-search-input" />
                        </div>
                        <button className="cp-notif-btn"><FaBell /></button>
                        <div className="cp-user-pill-wrap">
                            <button className="cp-user-pill" onClick={() => setUserMenuOpen(!userMenuOpen)}>
                                <span className="cp-user-icon"><FaUserAstronaut /></span>
                                <FaChevronDown className={userMenuOpen ? 'open' : ''} />
                            </button>
                        </div>
                    </div>
                </header>

                <div className="cp-scroll-area">
                    {/* Welcome & New Project */}
                    <div className="cp-welcome-block">
                        <div className="cp-welcome-text">
                            <h1 className="cp-title">Gestión de Proyectos Clínicos</h1>
                            <p className="cp-subtitle">Bienvenido, Dr. Investigador. Aquí está el resumen de sus ensayos actuales.</p>
                        </div>
                        <button className="cp-primary-btn" onClick={() => navigate('/protocol-editor')}>
                            <FaPlus /> Nuevo Protocolo
                        </button>
                    </div>

                    {/* Stats Grid */}
                    <div className="cp-stats-grid">
                        {STATS.map(stat => (
                            <div key={stat.label} className="cp-stat-card">
                                <div className="cp-stat-header">
                                    <span className="cp-stat-label">{stat.label}</span>
                                    <span className="cp-stat-icon" style={{ color: stat.color }}>{stat.icon}</span>
                                </div>
                                <div className="cp-stat-body">
                                    <span className="cp-stat-value">{stat.value}</span>
                                    <span className="cp-stat-trend" style={{ color: stat.color === '#ef4444' ? '#ef4444' : '#22c55e' }}>
                                        {stat.trend}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="cp-main-grid">
                        {/* Ensayos Column */}
                        <div className="cp-trials-col">
                            <h3 className="cp-section-title">
                                <FaFlask className="cp-sec-title-icon" /> Ensayos Clínicos en Curso
                            </h3>

                            <div className="cp-trials-list">
                                {CLINICAL_TRIALS.map(trial => (
                                    <div key={trial.id} className="cp-trial-card">
                                        <div className="cp-trial-main">
                                            <div className="cp-trial-meta">
                                                <span
                                                    className="cp-status-badge"
                                                    style={{ color: trial.statusColor, borderColor: trial.statusColor }}
                                                >
                                                    {trial.status}
                                                </span>
                                                <span className="cp-trial-id">ID: {trial.id}</span>
                                            </div>
                                            <h4 className="cp-trial-name">{trial.title}</h4>
                                            <p className="cp-trial-desc">{trial.description}</p>

                                            <div className="cp-trial-progress-row">
                                                <div className="cp-progress-left">
                                                    <span className="cp-prog-label">Reclutamiento de Pacientes</span>
                                                    <div className="cp-progress-bar-bg">
                                                        <div
                                                            className="cp-progress-bar-fill"
                                                            style={{
                                                                width: `${(trial.patients / trial.totalPatients) * 100}%`,
                                                                backgroundColor: trial.statusColor
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <span className="cp-prog-numbers"><strong>{trial.patients}</strong> / {trial.totalPatients}</span>
                                            </div>

                                            <div className="cp-trial-footer">
                                                <div className="cp-milestone">
                                                    <span className="cp-ms-label">Próximo Hito: {trial.nextMilestone}</span>
                                                    <div className="cp-ms-dots">
                                                        <span className="cp-ms-dot active"><FaUserCheck /></span>
                                                        <span className="cp-ms-dot active"><FaUserCheck /></span>
                                                        <span className="cp-ms-dot active"><FaUserCheck /></span>
                                                        <span className="cp-ms-dot"></span>
                                                        <span className="cp-ms-pct">{trial.progress}% Completado</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="cp-trial-actions">
                                            <button className={`cp-action-btn secondary${trial.finished ? ' disabled' : ''}`} disabled={trial.finished} onClick={() => !trial.finished && navigate('/project-budget')}>
                                                <FaWallet /> Presupuesto
                                            </button>
                                            <button className={`cp-action-btn secondary${trial.finished ? ' disabled' : ''}`} disabled={trial.finished} onClick={() => !trial.finished && navigate('/study-tasks')}>
                                                <FaQuestionCircle /> Tareas
                                            </button>
                                            <button className={`cp-action-btn primary${trial.finished ? ' disabled' : ''}`} disabled={trial.finished} onClick={() => !trial.finished && navigate('/ecrf-builder')}>
                                                <FaWrench /> Construir eCRF
                                            </button>
                                            <button className={`cp-action-btn primary${trial.finished ? ' disabled' : ''}`} disabled={trial.finished} onClick={() => !trial.finished && navigate('/ecrf-capture')}>
                                                <FaKeyboard /> Captura de Datos eCRF
                                            </button>
                                            <button className={`cp-action-btn secondary${trial.finished ? ' disabled' : ''}`} disabled={trial.finished} onClick={() => !trial.finished && navigate('/data-analysis')}>
                                                <FaChartLine /> Análisis de Datos
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right Sidebar */}
                        <aside className="cp-sidebar-right">
                            {/* AI Insights Section */}
                            <div className="cp-ai-card">
                                <div className="cp-ai-header">
                                    <span className="cp-ai-title-wrap">
                                        <FaRocket className="cp-spark" />
                                        <h4 className="cp-ai-title">EcoIA Insights</h4>
                                    </span>
                                    <FaCog className="cp-cog-icon" />
                                </div>
                                <div className="cp-ai-list">
                                    {AI_INSIGHTS.map((ins, idx) => (
                                        <div key={idx} className="cp-ai-item">
                                            <h5 className="cp-ai-item-title">{ins.title}</h5>
                                            <p className="cp-ai-item-desc">{ins.desc}</p>
                                            {ins.link && <button className="cp-ai-link">{ins.link}</button>}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Quick Resources */}
                            <div className="cp-resources-card">
                                <h4 className="cp-resources-title">RECURSOS RÁPIDOS</h4>
                                <div className="cp-resources-list">
                                    {QUICK_RESOURCES.map(res => (
                                        <button key={res.label} className="cp-resource-item">
                                            <span className="cp-res-icon">{res.icon}</span>
                                            <span className="cp-res-label">{res.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </aside>
                    </div>

                    <footer className="cp-footer">
                        <p>© 2024 Eco ScienceInnova - Plataforma de Gestión de Investigación Clínica Segura.</p>
                    </footer>
                </div>
            </div>
        </div>
    );
};

export default ClinicalProjects;
